import {Trans} from '@lingui/macro';
import {top} from '@popperjs/core';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {LearnMoreButton} from '../../../../../components/common/callToAction/learnMoreButton';
import {EducationTooltipCoordinator} from '../../../../../components/common/tooltips/educationTooltip/educationTooltipCoordinator';

/*
 * Constants.
 */

const subtitleWidth = 436; // px

/*
 * Props.
 */

interface DefaultVideoConferencingSectionProps {
  title: ReactNode;
  subtitle: ReactNode;
  bottom: ReactNode;
  className?: string;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'subtitle'
    'bottom';
  grid-template-columns: 1fr;
  grid-template-rows: repeat(2, auto);
  grid-row-gap: 8px;
  align-items: center;

  border-top: 1px solid #e6e6e6;
  padding-top: 19px;
  padding-bottom: 19px;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
  font-size: ${fontSizes.large};
  line-height: 1;
  color: ${(p) => p.theme.greys.shade90};
  display: flex;
  align-items: center;
`;

const StyledSubtitleDiv = styled.div`
  grid-area: subtitle;
  font-size: ${fontSizes.medium};
  line-height: 1.6;
  color: ${(p) => p.theme.greys.shade60};
  width: ${subtitleWidth}px;
`;

const StyledBottomDiv = styled.div`
  grid-area: bottom;
  display: grid;
  grid-row-gap: 12px;
  grid-template-column: 1fr;
`;

const StyledLabelIconDiv = styled.div`
  display: inline-block;
  padding-left: 6px;
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const DefaultVideoConferencingSection: FC<DefaultVideoConferencingSectionProps> = (props) => (
  <StyledDiv className={props.className}>
    <StyledTitleDiv>
      {props.title}
      <StyledLabelIconDiv>
        <EducationTooltipCoordinator
          placement={top}
          title={<Trans id="default-conferencing-tooltip-title">Video conference options</Trans>}
          tooltip={
            <Trans id="default-conferencing-tooltip-body">
              Available options depend on the video conference types that are enabled for your team and your
              Google or Office365 calendars.
            </Trans>
          }
          buttons={[
            <LearnMoreButton
              size={VisualSizesEnum.SMALL}
              variant={ButtonVariantsEnum.PRIMARY}
              helpPageUrl="https://help.front.com/t/35hha8j/use-zoom-google-hangouts-microsoft-teams-and-skype-meetings-videoconferencing-in-front-calendar"
            />,
          ]}
          borderRadius={16}
        />
      </StyledLabelIconDiv>
    </StyledTitleDiv>
    <StyledSubtitleDiv>{props.subtitle}</StyledSubtitleDiv>
    <StyledBottomDiv>{props.bottom}</StyledBottomDiv>
  </StyledDiv>
);
